import { NextPage } from 'next';
import dynamic from 'next/dynamic';

import type { LandingResults } from 'src/common/types/api/v4/search/v1/landing';
import type { LandingRedirect } from 'src/search/utils/serverGetLandingPageData';

import { GEOLOCATION_COOKIE } from 'src/common/constants';
import serverGetLandingPageData, { isLandingRedirect } from 'src/search/utils/serverGetLandingPageData';
import { validateParams } from 'src/common/utils/validators';
import Layout from 'src/common/components/Layout';
import Header from 'src/common/components/Header';
import GdprCompliance from 'src/common/components/GdprCompliance';
import ApiResponseError from 'src/common/utils/apiResponseError';
import { AuthUserApiResponse } from 'src/common/types/api/v4/auth/user';
import { SearchResults } from 'src/common/types/api/v4/boats/search';
import { SeoData } from 'src/common/types/api/v4/rutter/v1/search/seo';
import serverGetUser from 'src/common/utils/serverGetUser';
import mapSlugsToParams from 'src/search/utils/mapSlugsToQuery';
import serverGetCategorySeo from 'src/search/utils/serverGetCategorySeo';
import serverGetSearchResults from 'src/search/utils/serverGetSearchResults';
import serverGetLocationSeo from 'src/search/utils/serverGetLocationSeo';
import serverGetNearbySeo from 'src/search/utils/serverGetNearbySeo';
import isValidCategoryLocation from 'src/search/utils/isValidCategoryLocation';
import useUpdateDataToMatchCurrency from 'src/common/hooks/useUpdateDataToMatchCurrency';
import handleApiErrors from 'src/common/utils/handleApiErrors';
import withCommonMiddleware from 'src/common/utils/withCommonMiddleware';
import hasGeometry from 'src/search/utils/hasGeometry';
import optimizeListingDataForSearch from 'src/search/utils/optimizeListingDataForSearch';
import { FavoriteApiResponse } from 'src/common/types/api/v4/favorites';
import serverGetFavorites from 'src/common/utils/serverGetFavorites';
import { FavoritesProvider } from 'src/common/providers/FavoritesProvider';
import serverGetInquiryIndicators from 'src/common/utils/serverGetInquiryIndicators';
import { InquirySentIndicatorApiResponse } from 'src/common/types/api/v4/trips';
import { InquiryIndicatorsProvider } from 'src/common/providers/InquiryIndicatorsProvider';
import urlRedirects from 'src/search/utils/urlRedirects';
import Footer from 'src/common/components/Footer';
import FooterLinks from 'src/common/components/FooterLinks';
import ReviewAndAppLinks from 'src/common/components/ReviewAndAppLinks';
import SubFooter from 'src/common/components/SubFooter';
import MainContentPlaceholder from 'src/common/components/MainContentPlaceholder';
import prependLocale from 'src/common/utils/prependLocale';
import SearchInputProvider from 'src/common/components/Search/SearchInputProvider';
import handleSecondaryContentApiFailures from 'src/common/utils/handleSecondaryContentApiFailures';
import SearchHeroVisibilityProvider from 'src/common/components/Search/SearchHeroVisibilityProvider';
import MobileWebViewHeader from 'src/common/components/MobileWebViewHeader';
import HeaderSticky from 'src/common/components/HeaderSticky';
import getCookie from 'src/common/utils/getCookie';
import { loadTranslations } from 'src/common/utils/serverSidePropsHelpers';

const DlpRoot = dynamic(
  () => import('src/search/components/DlpRoot'),
  { loading: () => <MainContentPlaceholder /> },
);
const SearchPageRoot = dynamic(
  () => import('src/search/components/SearchPageRoot'),
  { loading: () => <MainContentPlaceholder /> },
);

type SearchPageProps = {
  user: Partial<AuthUserApiResponse>;
  favorites: FavoriteApiResponse | null;
  inquiryIndicators: InquirySentIndicatorApiResponse;
  searchResults: SearchResults;
  seo: SeoData;
  isNearbyLocationSearch: boolean;
  willPerformGeolocationSearch: boolean;
  landingPageData: LandingResults;
  isDlpPage: boolean;
};

const SearchPage: NextPage<SearchPageProps> = ({
  searchResults,
  seo,
  user,
  favorites,
  inquiryIndicators,
  isNearbyLocationSearch,
  willPerformGeolocationSearch,
  landingPageData,
  isDlpPage,
}) => {
  const currencyCode = landingPageData?.recommended_listings?.[0]?.request_currency?.code;
  useUpdateDataToMatchCurrency(currencyCode, isDlpPage);

  return (
    <Layout
      withFixedHeader={isDlpPage}
      includeCommonMetaTags={false}
      overscrollColor={isDlpPage ? 'ivory' : undefined}
    >
      {/* Only show the Cookie banner if the user is logged in */}
      {!user?.id && <GdprCompliance />}
      <FavoritesProvider value={favorites}>
        <InquiryIndicatorsProvider value={inquiryIndicators.results}>
          <SearchInputProvider
            placeId={searchResults.place_id}
            slug={searchResults.slug}
            breadcrumbs={searchResults.breadcrumbs}
          >
            {isDlpPage
              ? (
                <SearchHeroVisibilityProvider>

                  <MobileWebViewHeader />
                  <HeaderSticky
                    variant="ivory"
                    showBottomBorderOnScroll
                  />
                  <DlpRoot
                    content={landingPageData}
                    isNearbyLocationSearch={isNearbyLocationSearch}
                    seo={seo}
                  />
                </SearchHeroVisibilityProvider>
              )
              : (
                <>
                  <Header fixed={false} withSearch />
                  <SearchPageRoot
                    searchResults={searchResults}
                    seo={seo}
                    isNearbyLocationSearch={isNearbyLocationSearch}
                    willPerformGeolocationSearch={willPerformGeolocationSearch}
                  />
                </>
              )}
            <Footer>
              <FooterLinks />
              <ReviewAndAppLinks />
              <SubFooter />
            </Footer>
          </SearchInputProvider>
        </InquiryIndicatorsProvider>
      </FavoritesProvider>
    </Layout>
  );
};

/**
 * Check if a category page is empty and there is also a location,
 * it's ok to redirect to the location page to avoid lower SEO ranking.
 */
const shouldRedirectToLocation = (query) => {
  if (!query.category_slug || !query.location) return false;

  const ignoreKeys = ['category_slug', 'location', 'page_size', 'slug'];
  const queryKeys = Object.keys(query).filter((key) => !ignoreKeys.includes(key));
  // if there are other params then don't redirect
  return queryKeys.length === 0;
};

type Params = {};
type Query = {
  category_slug?: string;
  location?: string;
  place_id?: string;
  page?: string;
  page_size?: string;
};

export const getServerSideProps = handleApiErrors(
  withCommonMiddleware<SearchPageProps, Params, Query>(async (context) => {
    // Redirect malformed salvageable urls
    const maybeRedirect = urlRedirects(context);
    if (maybeRedirect) {
      return maybeRedirect;
    }

    if (!validateParams(context.query)) {
      // This is using the ApiResponseError to allow us to return a 400 status.
      throw new ApiResponseError(
        new Response('{"error":"bad request"}', {
          status: 400,
          statusText: 'Bad Request',
        }),
      );
    }

    const translations = loadTranslations(
      context.locale,
      [
        'common',
        'listing-page',
        'search-page',
        'search-and-filters',
        'navbar',
        'destination-navigation',
        'boat-categories',
        'discovery-landing-pages',
        'boat-type-cards',
        'location-overrides',
        'how-it-works-section',
        'footer',
      ],
    );

    let user: Partial<AuthUserApiResponse> = {};
    let favorites: FavoriteApiResponse | null = null;
    let inquiryIndicators: InquirySentIndicatorApiResponse = {
      results: [],
    };
    let searchResults: SearchResults = {};
    let seo: SeoData = {};
    let landingPageData: LandingResults | LandingRedirect = {};

    mapSlugsToParams(context);
    context.query.page_size = '24';

    // Avoid calling the API if the category and location combos are invalid.
    if (!isValidCategoryLocation(context)) {
      return {
        notFound: true,
      };
    }

    const isNearbyLocationSearch = (
      !context.query.location
      && !hasGeometry(context.query)
      && !context.query.place_id
    );

    let willPerformGeolocationSearch = false;
    if (
      // Web and Mobile apps with Mobile Native Webview set this cookie
      getCookie(context, GEOLOCATION_COOKIE) === 'true'
      && isNearbyLocationSearch
    ) {
      willPerformGeolocationSearch = true;
    }

    // Universal api calls
    const promises = [
      serverGetUser(context).then((data) => {
        user = data;
      }),
      serverGetFavorites(context).then((data) => {
        favorites = data;
      }).catch(handleSecondaryContentApiFailures),
      serverGetInquiryIndicators(context).then((data) => {
        inquiryIndicators = data;
      }),
    ];

    // The location seo data is always the preferred content when location data
    // is available. Fallback to nearby content when there is no location data.
    // If there is geometry included already, fallback to category content
    if (context.query.location) {
      promises.push(
        serverGetLocationSeo(context).then((data) => { seo = data; }),
      );
    } else if (isNearbyLocationSearch) {
      promises.push(
        serverGetNearbySeo(context).then((data) => { seo = data; }),
      );
    } else {
      promises.push(
        serverGetCategorySeo(context).then((data) => { seo = data; }),
      );
    }

    const pageNumber: number = (context.query.page && Number(context.query.page)) || 0;

    /**
     * If a `/boat-rental/` url does not have a `?page` query, it is a DLP page
     */
    const isDlpPage = pageNumber === 0;

    // Search api calls
    if (isDlpPage) {
      // Display discovery landing page content
      // Landing pages contain own SEO content
      promises.push(
        serverGetLandingPageData(context).then((data) => {
          landingPageData = data;
        }),
      );
    } else {
      // Internal Search Only
      // Display search results
      promises.push(
        serverGetSearchResults(context).then((data) => ({
          ...data,
          results: data.results?.map(
            optimizeListingDataForSearch(10),
          ) ?? [],
          instabook_listings: data.instabook_listings?.map(
            optimizeListingDataForSearch(0),
          ) ?? [],
          top_categories: data.top_categories?.map((category) => ({
            ...category,
            results: category.results?.map(
              optimizeListingDataForSearch(0),
            ),
          })) ?? [],
        }))
          .then((data) => { searchResults = data; }),
      );
    }

    await Promise.all(promises);

    // redirects
    const searchResultsCount = searchResults?.results?.length ?? 0;
    const hasSearchResults = searchResultsCount > 0;
    if (!hasSearchResults && pageNumber > 1) {
      return {
        redirect: {
          destination: context.resolvedUrl.replace(new RegExp(`page=${pageNumber}`), 'page=1'),
          statusCode: 302,
        },
      };
    }

    if (isLandingRedirect(landingPageData)) {
      return {
        redirect: {
          destination: prependLocale(landingPageData.redirect_url, context.locale),
          statusCode: landingPageData.code,
        },
      };
    }

    if (!hasSearchResults && !landingPageData && shouldRedirectToLocation(context.query)) {
      return {
        redirect: {
          destination: context.resolvedUrl.replace(new RegExp(`${context.query.category_slug}\\/?`), ''),
          statusCode: 302,
        },
      };
    }

    // Redirect to 404 when dlp data is empty, meaning, the given location is invalid or a typo
    // and no search results, in order to prevent dlp page from showing unhelpful content
    if (isDlpPage && Object.keys(landingPageData).length === 0 && !hasSearchResults) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        user,
        favorites,
        inquiryIndicators,
        searchResults,
        seo,
        landingPageData,
        isNearbyLocationSearch,
        willPerformGeolocationSearch,
        isDlpPage,
        ...(await translations),
      },
    };
  }),
);

export default SearchPage;
